/**
 * CyberSells Equipment System Module
 * 
 * Implements a pixel-styled RPG equipment console where customers can
 * equip store products into character slots and see real-time stats.
 * 
 * @module equipment
 */

(function() {
  'use strict';

  /**
   * Equipment slots configuration
   */
  const SLOTS = {
    head: { label: 'Head', icon: '?', order: 1 },
    chest: { label: 'Chest', icon: '?', order: 2 },
    legs: { label: 'Legs', icon: '?', order: 3 },
    hands: { label: 'Hands', icon: '?', order: 4 },
    feet: { label: 'Feet', icon: '?', order: 5 },
    accessory1: { label: 'Accessory 1', icon: '?', order: 6 },
    accessory2: { label: 'Accessory 2', icon: '?', order: 7 }
  };

  /**
   * Stat configuration
   */
  const STATS = {
    str: { label: 'STR', icon: '⚡', color: 'red' },
    agi: { label: 'AGI', icon: '▲', color: 'lime' },
    agility: { label: 'AGI', icon: '▲', color: 'lime' },  // Alias for agi
    luck: { label: 'LUCK', icon: '★', color: 'lime' },
    int: { label: 'INT', icon: '◉', color: 'cyan' },
    tech: { label: 'TECH', icon: '◎', color: 'cyan' },
    style: { label: 'STYLE', icon: '●', color: 'purple' },
    perception: { label: 'PER', icon: '◎', color: 'cyan' },  // Added
    damage: { label: 'DMG', icon: '⚡', color: 'red' },      // Added
    defense: { label: 'DEF', icon: '◈', color: 'cyan' },    // Added
    speed: { label: 'SPD', icon: '▲', color: 'lime' },      // Added
    vit: { label: 'VIT', icon: '■', color: 'red' }          // Added
  };

  /**
   * Current loadout state
   */
  let currentLoadout = {};

  /**
   * Console container element
   */
  let consoleEl = null;

  /**
   * Initialize equipment console
   * 
   * @param {Object} options - Configuration options
   * @param {string} [options.storageKey='cybersells_loadout'] - localStorage key
   * @param {boolean} [options.autoSave=true] - Auto-save on changes
   */
  function setupEquipmentConsole(options = {}) {
    const config = {
      storageKey: options.storageKey || 'cybersells_loadout',
      autoSave: options.autoSave !== false
    };

    console.log('[Equipment] setupEquipmentConsole called with config:', config);

    // Find console element (optional - event listeners work without it)
    consoleEl = document.querySelector('[data-cs-console]');
    
    if (!consoleEl) {
      console.log('[Equipment] Console element not found, but event listeners will still work');
    } else {
      console.log('[Equipment] Console element found');
    }

    // Load saved loadout
    loadLoadout(config.storageKey);

    // Setup event listeners (ALWAYS, even without console)
    setupEventListeners(config);
    console.log('[Equipment] Event listeners attached');

    // Initial render (only if console exists)
    if (consoleEl) {
      renderConsole();
      
      // Update with cart preview
      updateCartPreview();
    }

    console.log('[Equipment] Setup complete. Current loadout:', currentLoadout);
  }

  /**
   * Setup event listeners for equip/unequip actions
   * 
   * @param {Object} config - Configuration
   */
  function setupEventListeners(config) {
    // Equip buttons (on product pages/cards)
    document.addEventListener('click', (e) => {
      const equipBtn = e.target.closest('[data-cs-equip]');
      if (!equipBtn) return;

      e.preventDefault();
      handleEquipClick(equipBtn, config);
    });

    // Unequip buttons (in console)
    if (consoleEl) {
      consoleEl.addEventListener('click', (e) => {
        const unequipBtn = e.target.closest('[data-cs-unequip]');
        if (!unequipBtn) return;

        e.preventDefault();
        const slot = unequipBtn.dataset.csUnequip;
        unequipSlot(slot, config);
      });
    }

    // CART REFRESH BUTTON
    document.addEventListener('click', (e) => {
      const refreshBtn = e.target.closest('[data-cs-cart-refresh]');
      if (!refreshBtn) return;

      e.preventDefault();
      console.log('[Equipment] Manual cart refresh triggered');
      refreshBtn.textContent = '⏳ Refreshing...';
      
      updateCartPreview().then(() => {
        refreshBtn.textContent = '✅ Updated!';
        setTimeout(() => {
          refreshBtn.textContent = '🔄 Refresh Cart Preview';
        }, 2000);
      }).catch((err) => {
        console.error('[Equipment] Refresh failed:', err);
        refreshBtn.textContent = '❌ Failed';
        setTimeout(() => {
          refreshBtn.textContent = '🔄 Refresh Cart Preview';
        }, 2000);
      });
    });

    // Keyboard navigation
    if (consoleEl) {
      consoleEl.addEventListener('keydown', handleKeyboardNav);
    }
  }

  /**
   * Handle equip button click
   * - Equips item to loadout
   * - Adds item to cart (NEW!)
   * 
   * @param {HTMLElement} btn - Equip button
   * @param {Object} config - Configuration
   */
  function handleEquipClick(btn, config) {
    console.log('[Equipment] Button clicked:', btn);
    
    try {
      const payload = {
        productId: btn.dataset.productId || btn.dataset.csEquip,
        variantId: btn.dataset.variantId,
        title: btn.dataset.title || btn.getAttribute('aria-label') || 'Item',
        price: btn.dataset.price || '0',
        slot: btn.dataset.slot || 'accessory1',
        stats: JSON.parse(btn.dataset.stats || '{}'),
        styleTags: JSON.parse(btn.dataset.style || '{}'),
        pixelIcon: btn.dataset.icon || '',
        image: btn.dataset.icon || ''  // Store image URL for persistence
      };

      console.log('[Equipment] Payload:', payload);

      // 1. Equip to loadout
      equipItem(payload, config);

      // 2. Add to cart (NEW!)
      if (payload.variantId) {
        addToCart(payload.variantId, btn);
      } else {
        console.warn('[Equipment] No variantId - cannot add to cart');
      }

      // Visual feedback
      btn.classList.add('cs-equipped');
      setTimeout(() => btn.classList.remove('cs-equipped'), 300);

      // Announce to screen reader
      if (window.CyberSells_accessibility?.announceToScreenReader) {
        window.CyberSells_accessibility.announceToScreenReader(
          `${payload.title} equipped to ${SLOTS[payload.slot]?.label || payload.slot} and added to cart`
        );
      }
      
      console.log('[Equipment] Item equipped successfully');
    } catch (error) {
      console.error('[Equipment] Error equipping item:', error);
    }
  }

  /**
   * Add item to Shopify cart
   * 
   * @param {string} variantId - Product variant ID
   * @param {HTMLElement} btn - Button element for feedback
   */
  function addToCart(variantId, btn) {
    console.log('[Equipment] Adding to cart:', variantId);

    // Update button text temporarily
    const originalText = btn.textContent;
    btn.textContent = '⏳ Adding...';
    btn.disabled = true;

    // Shopify Cart API: Add item
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Cart API error: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log('[Equipment] ✅ Added to cart:', data);
        
        // Success feedback
        btn.textContent = '✅ Equipped & Added!';
        btn.classList.add('cs-cart-success');
        
        // Update cart preview
        updateCartPreview();

        // Trigger cart drawer/notification (if theme has it)
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        
        // Show toast notification
        if (window.CyberSells_toast?.show) {
          window.CyberSells_toast.show(
            `${data.product_title} equipped and added to cart!`,
            'success'
          );
        }

        // Reset button after delay
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
          btn.classList.remove('cs-cart-success');
        }, 2000);
      })
      .catch(error => {
        console.error('[Equipment] ❌ Cart add failed:', error);
        
        // Error feedback
        btn.textContent = '❌ Cart Error';
        btn.classList.add('cs-cart-error');
        
        // Show toast notification
        if (window.CyberSells_toast?.show) {
          window.CyberSells_toast.show(
            'Failed to add to cart. Try again.',
            'error'
          );
        }

        // Reset button after delay
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
          btn.classList.remove('cs-cart-error');
        }, 2000);
      });
  }

  /**
   * Equip an item to a slot
   * 
   * @param {Object} payload - Item data
   * @param {Object} config - Configuration
   */
  function equipItem(payload, config) {
    const { slot } = payload;

    if (!SLOTS[slot]) {
      console.warn(`[Equipment] Invalid slot: ${slot}`);
      return;
    }

    // Replace item in slot
    currentLoadout[slot] = payload;

    // Auto-save
    if (config.autoSave) {
      saveLoadout(config.storageKey);
    }

    // Re-render
    renderConsole();

    if (window.cyberSellsConfig?.debug) {
      console.debug('[Equipment] Item equipped:', payload);
    }
  }

  /**
   * Unequip an item from a slot
   * 
   * @param {string} slot - Slot name
   * @param {Object} config - Configuration
   */
  function unequipSlot(slot, config) {
    if (!currentLoadout[slot]) return;

    const itemTitle = currentLoadout[slot].title;
    delete currentLoadout[slot];

    // Auto-save
    if (config.autoSave) {
      saveLoadout(config.storageKey);
    }

    // Re-render
    renderConsole();

    // Announce to screen reader
    if (window.CyberSells_accessibility?.announceToScreenReader) {
      window.CyberSells_accessibility.announceToScreenReader(
        `${itemTitle} unequipped from ${SLOTS[slot]?.label || slot}`
      );
    }

    if (window.cyberSellsConfig?.debug) {
      console.debug('[Equipment] Item unequipped from', slot);
    }
  }

  /**
   * Compute total stats from equipped items
   * 
   * @param {Object} loadout - Current loadout
   * @returns {Object} Total stats
   */
  function computeTotals(loadout = currentLoadout) {
    const totals = {
      str: 0,
      agi: 0,
      luck: 0,
      int: 0,
      tech: 0,
      style: 0,
      vit: 0,
      damage: 0,
      defense: 0,
      speed: 0,
      perception: 0
    };

    Object.values(loadout).forEach(item => {
      if (item.stats) {
        Object.keys(totals).forEach(stat => {
          totals[stat] += (item.stats[stat] || 0);
        });
      }
    });

    return totals;
  }

  /**
   * Compute match score based on outfit cohesion
   * 
   * Algorithm:
   * - For each pair of items, check compatibility
   * - STYLE-BASED (if styleTags exist):
   *   - +25 if outlineColor matches
   *   - +20 if palette matches
   *   - +10 if primaryColor complementary/same
   *   - +10 if vibe matches
   *   - -15 if primaryColor clashes
   *   - -10 if contrast conflicts
   * - STAT-BASED (fallback if no styleTags):
   *   - +15 per shared stat between items
   *   - Higher overlap = higher score
   * 
   * @param {Object} loadout - Current loadout
   * @returns {Object} { score: 0-100, label: string }
   */
  function computeMatchScore(loadout = currentLoadout) {
    const items = Object.values(loadout);
    const itemCount = items.length;

    if (itemCount === 0) {
      return { score: 0, label: 'Empty' };
    }

    if (itemCount === 1) {
      return { score: 100, label: 'Solo' };
    }

    let totalPoints = 0;
    let maxPoints = 0;
    let usingStatFallback = false;

    // Compare each pair of items
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const item1 = items[i];
        const item2 = items[j];

        const style1 = item1.styleTags || {};
        const style2 = item2.styleTags || {};
        const stats1 = item1.stats || {};
        const stats2 = item2.stats || {};

        let pairPoints = 0;
        let pairMaxPoints = 75; // Max possible points per pair

        // Check if items have style tags
        const hasStyleTags = Object.keys(style1).length > 0 && Object.keys(style2).length > 0;

        if (hasStyleTags) {
          // STYLE-BASED SCORING
          // Outline color match (+25)
          if (style1.outlineColor && style2.outlineColor) {
            if (style1.outlineColor === style2.outlineColor) {
              pairPoints += 25;
            }
          }

          // Palette match (+20)
          if (style1.palette && style2.palette) {
            if (style1.palette === style2.palette) {
              pairPoints += 20;
            }
          }

          // Primary color compatibility (+10 or -15)
          if (style1.primaryColor && style2.primaryColor) {
            if (isColorCompatible(style1.primaryColor, style2.primaryColor)) {
              pairPoints += 10;
            } else if (isColorClashing(style1.primaryColor, style2.primaryColor)) {
              pairPoints -= 15;
            }
          }

          // Vibe match (+10)
          if (style1.vibe && style2.vibe) {
            if (style1.vibe === style2.vibe) {
              pairPoints += 10;
            }
          }

          // Contrast conflict (-10)
          if (style1.contrast && style2.contrast) {
            if (isContrastConflict(style1.contrast, style2.contrast)) {
              pairPoints -= 10;
            }
          }
        } else {
          // STAT-BASED SCORING (Fallback)
          usingStatFallback = true;
          pairMaxPoints = 60; // Lower max for stat-based

          // Count shared stats with non-zero values
          const sharedStats = Object.keys(stats1).filter(stat => {
            return stats1[stat] > 0 && stats2[stat] > 0;
          });

          // Award points for shared stats (+15 per shared stat)
          pairPoints += sharedStats.length * 15;

          // Bonus for high overlap (+20 if 3+ shared stats)
          if (sharedStats.length >= 3) {
            pairPoints += 20;
          }

          // Small penalty if NO shared stats (-10)
          if (sharedStats.length === 0) {
            pairPoints -= 10;
          }

          if (window.cyberSellsConfig?.debug) {
            window.console.log(`[Equipment] Stat-based pair: ${sharedStats.length} shared stats`, sharedStats);
          }
        }

        totalPoints += pairPoints;
        maxPoints += pairMaxPoints;
      }
    }

    // Normalize to 0-100
    const score = maxPoints > 0 ? Math.max(0, Math.min(100, (totalPoints / maxPoints) * 100)) : 0;
    const roundedScore = Math.round(score);

    // Determine label
    let label = 'Mixed';
    if (roundedScore >= 80) label = usingStatFallback ? 'Synergy' : 'Cohesive';
    else if (roundedScore >= 60) label = usingStatFallback ? 'Compatible' : 'Harmonious';
    else if (roundedScore >= 40) label = 'Decent';
    else if (roundedScore >= 20) label = 'Mixed';
    else label = 'Clashing';

    if (window.cyberSellsConfig?.debug) {
      window.console.log(`[Equipment] Match score: ${roundedScore}% (${label})`, {
        method: usingStatFallback ? 'stat-based' : 'style-based',
        totalPoints,
        maxPoints
      });
    }

    return { score: roundedScore, label };
  }

  /**
   * Check if two colors are compatible
   * 
   * @param {string} color1 - First color
   * @param {string} color2 - Second color
   * @returns {boolean} Compatible
   */
  function isColorCompatible(color1, color2) {
    // Same color
    if (color1 === color2) return true;

    // Define color families
    const families = {
      warm: ['red', 'orange', 'yellow', 'pink', 'coral'],
      cool: ['blue', 'cyan', 'teal', 'green', 'purple'],
      neutral: ['black', 'white', 'gray', 'grey', 'silver', 'gold'],
      neon: ['neon-cyan', 'neon-lime', 'neon-pink', 'neon-purple']
    };

    // Check if both in same family
    for (const family of Object.values(families)) {
      if (family.includes(color1) && family.includes(color2)) {
        return true;
      }
    }

    // Neutrals go with everything
    if (families.neutral.includes(color1) || families.neutral.includes(color2)) {
      return true;
    }

    return false;
  }

  /**
   * Check if two colors clash
   * 
   * @param {string} color1 - First color
   * @param {string} color2 - Second color
   * @returns {boolean} Clashing
   */
  function isColorClashing(color1, color2) {
    // Warm + cool can clash
    const warm = ['red', 'orange', 'yellow', 'pink'];
    const cool = ['blue', 'cyan', 'teal'];

    const color1IsWarm = warm.includes(color1);
    const color2IsCool = cool.includes(color2);

    const color1IsCool = cool.includes(color1);
    const color2IsWarm = warm.includes(color2);

    return (color1IsWarm && color2IsCool) || (color1IsCool && color2IsWarm);
  }

  /**
   * Check if contrasts conflict
   * 
   * @param {string} contrast1 - First contrast level
   * @param {string} contrast2 - Second contrast level
   * @returns {boolean} Conflicting
   */
  function isContrastConflict(contrast1, contrast2) {
    // High + low conflict
    if (contrast1 === 'high' && contrast2 === 'low') return true;
    if (contrast1 === 'low' && contrast2 === 'high') return true;
    return false;
  }

  /**
   * Render the console UI
   */
  function renderConsole() {
    if (!consoleEl) return;

    const totals = computeTotals();
    const matchScore = computeMatchScore();

    // Render slots
    renderSlots();

    // Render stats
    renderStats(totals);

    // Render match score
    renderMatchScore(matchScore);
  }

  /**
   * Render equipment slots
   */
  function renderSlots() {
    const slotsContainer = consoleEl.querySelector('[data-cs-slots]');
    if (!slotsContainer) return;

    const slotsHTML = Object.entries(SLOTS)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([slotKey, slotConfig]) => {
        const item = currentLoadout[slotKey];
        
        if (item) {
          return `
            <div class="cs-slot cs-slot-filled" data-slot="${slotKey}">
              <div class="cs-slot-icon">
                ${item.pixelIcon ? `<img src="${item.pixelIcon}" alt="${item.title}" class="cs-pixel-icon">` : slotConfig.icon}
              </div>
              <div class="cs-slot-info">
                <div class="cs-slot-label">${slotConfig.label}</div>
                <div class="cs-slot-title">${item.title}</div>
              </div>
              <button
                type="button"
                class="cs-slot-action cs-interactive"
                data-cs-unequip="${slotKey}"
                aria-label="Unequip ${item.title}"
              >
                <span class="cs-glow-red">�</span>
              </button>
            </div>
          `;
        } else {
          return `
            <div class="cs-slot cs-slot-empty" data-slot="${slotKey}">
              <div class="cs-slot-icon cs-slot-placeholder">
                <span class="cs-glow-cyan">${slotConfig.icon}</span>
              </div>
              <div class="cs-slot-info">
                <div class="cs-slot-label">${slotConfig.label}</div>
                <div class="cs-slot-empty-text">Empty</div>
              </div>
            </div>
          `;
        }
      })
      .join('');

    slotsContainer.innerHTML = slotsHTML;
  }

  /**
   * Render stat totals
   * 
   * @param {Object} totals - Stat totals
   */
  function renderStats(totals) {
    const statsContainer = consoleEl.querySelector('[data-cs-stats]');
    if (!statsContainer) return;

    const statsHTML = Object.entries(STATS)
      .map(([statKey, statConfig]) => {
        const value = totals[statKey] || 0;
        return `
          <div class="cs-stat-item cs-stat-${statConfig.color}">
            <span class="cs-stat-icon cs-glow-${statConfig.color}">${statConfig.icon}</span>
            <span class="cs-stat-label">${statConfig.label}</span>
            <span class="cs-stat-value cs-glow-${statConfig.color}">${value}</span>
          </div>
        `;
      })
      .join('');

    statsContainer.innerHTML = statsHTML;
  }

  /**
   * Render match score
   * 
   * @param {Object} matchScore - Match score data
   */
  function renderMatchScore(matchScore) {
    const scoreContainer = consoleEl.querySelector('[data-cs-match-score]');
    if (!scoreContainer) return;

    const { score, label } = matchScore;

    // Determine color based on score
    let colorClass = 'cs-glow-cyan';
    if (score >= 80) colorClass = 'cs-glow-lime';
    else if (score >= 60) colorClass = 'cs-glow-cyan';
    else if (score >= 40) colorClass = 'cs-glow-purple';
    else colorClass = 'cs-glow-red';

    scoreContainer.innerHTML = `
      <div class="cs-match-header">
        <span class="cs-glow-lime">? MATCH SCORE</span>
      </div>
      <div class="cs-match-display">
        <div class="cs-match-value ${colorClass}">${score}</div>
        <div class="cs-match-label">${label}</div>
      </div>
      <div class="cs-match-bar">
        <div class="cs-match-fill ${colorClass}" style="width: ${score}%"></div>
      </div>
    `;
  }

  /**
   * Handle keyboard navigation in console
   * 
   * @param {KeyboardEvent} e - Keyboard event
   */
  function handleKeyboardNav(e) {
    // Tab navigation is native, just add focus styles
    // Escape to close if console is a modal (future enhancement)
    if (e.key === 'Escape') {
      const focusedSlot = e.target.closest('.cs-slot');
      if (focusedSlot) {
        focusedSlot.querySelector('button')?.blur();
      }
    }
  }

  /**
   * Save loadout to localStorage
   * 
   * @param {string} key - Storage key
   */
  function saveLoadout(key) {
    try {
      localStorage.setItem(key, JSON.stringify(currentLoadout));
      if (window.cyberSellsConfig?.debug) {
        console.debug('[Equipment] Loadout saved');
      }
    } catch (error) {
      console.warn('[Equipment] Failed to save loadout:', error);
    }
  }

  /**
   * Load loadout from localStorage
   * 
   * @param {string} key - Storage key
   */
  function loadLoadout(key) {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        currentLoadout = JSON.parse(saved);
        if (window.cyberSellsConfig?.debug) {
          console.debug('[Equipment] Loadout loaded', currentLoadout);
        }
      }
    } catch (error) {
      console.warn('[Equipment] Failed to load loadout:', error);
      currentLoadout = {};
    }
  }

  /**
   * Get current loadout (public API)
   * 
   * @returns {Object} Current loadout
   */
  function getLoadout() {
    return { ...currentLoadout };
  }

  /**
   * Clear all equipped items
   * 
   * @param {Object} config - Configuration
   */
  function clearLoadout(config = {}) {
    currentLoadout = {};
    
    if (config.autoSave !== false) {
      saveLoadout(config.storageKey || 'cybersells_loadout');
    }

    renderConsole();

    // Dispatch event for other systems
    document.dispatchEvent(new CustomEvent('cybersells:equipment-changed', {
      detail: { action: 'clear' }
    }));

    if (window.cyberSellsConfig?.debug) {
      console.debug('[Equipment] Loadout cleared');
    }
  }

  /**
   * Fetch cart data from Shopify
   */
  async function fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('[Equipment] Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Update equipment console with cart preview
   * Shows items in cart as preview in equipment slots
   */
  async function updateCartPreview() {
    if (!consoleEl) {
      console.log('[Equipment] No console element, skipping cart preview');
      return;
    }

    const cart = await fetchCartData();
    if (!cart || !cart.items || cart.items.length === 0) {
      // No cart items - show regular loadout
      console.log('[Equipment] No cart items, rendering regular loadout');
      renderConsole();
      return;
    }

    console.log('[Equipment] Updating cart preview with', cart.items.length, 'items');

    // Parse cart items for equipment slots
    const cartEquipment = {};
    const cartStats = {};

    cart.items.forEach((item, index) => {
      console.log(`[Equipment] Parsing item ${index}:`, item.title);
      console.log(`[Equipment] Item properties:`, item.properties);

      // Check for equipment slot in multiple places
      let equipmentSlot = null;
      
      // Check properties object
      if (item.properties) {
        for (const [key, value] of Object.entries(item.properties)) {
          // Skip empty keys/values
          if (!key || value === null || value === undefined || value === '') continue;
          
          console.log(`[Equipment] Property: ${key} = ${value}`);
          
          // Check both with and without underscore prefix
          const normalizedKey = key.replace(/^_/, '').toLowerCase();
          if (normalizedKey === 'equipment_slot' || normalizedKey === 'equipmentslot') {
            equipmentSlot = value.toLowerCase().trim();
            console.log(`[Equipment] ✅ Found equipment slot: ${equipmentSlot}`);
          }
        }
      } else {
        console.log('[Equipment] ⚠️ No properties object on item');
      }

      // Also check direct property (Shopify sometimes uses this)
      if (!equipmentSlot && item.equipment_slot) {
        equipmentSlot = item.equipment_slot.toLowerCase().trim();
        console.log(`[Equipment] Found equipment slot on item: ${equipmentSlot}`);
      }
      
      // Fallback: If no slot found, log all properties for debugging
      if (!equipmentSlot && item.properties) {
        console.log('[Equipment] All item properties:', JSON.stringify(item.properties, null, 2));
      }

      if (equipmentSlot && SLOTS[equipmentSlot]) {
        console.log(`[Equipment] Adding ${item.title} to ${equipmentSlot} slot`);
        
        // Store in cart equipment
        if (!cartEquipment[equipmentSlot]) {
          cartEquipment[equipmentSlot] = [];
        }
        cartEquipment[equipmentSlot].push({
          title: item.product_title || item.title,
          quantity: item.quantity,
          isCart: true
        });
      } else {
        console.log(`[Equipment] No valid slot for ${item.title}`);
      }

      // Parse stats from properties
      if (item.properties) {
        for (const [key, value] of Object.entries(item.properties)) {
          // Skip empty or underscore-only keys
          if (!key || value === null || value === undefined || value === '') continue;
          
          // Normalize key: remove underscore prefix, "stat_" prefix, and lowercase
          const normalizedKey = key
            .replace(/^_/, '')               // Remove leading underscore
            .replace(/^stat_/, '')           // Remove "stat_" prefix
            .toLowerCase()
            .trim();
          
          const statValue = parseInt(value);
          
          if (!isNaN(statValue) && STATS[normalizedKey]) {
            cartStats[normalizedKey] = (cartStats[normalizedKey] || 0) + (statValue * item.quantity);
            console.log(`[Equipment] Added stat ${normalizedKey}: ${statValue} (from property "${key}")`);
          }
        }
      }
    });

    console.log('[Equipment] Cart equipment:', cartEquipment);
    console.log('[Equipment] Cart stats:', cartStats);

    // Render equipment slots with cart preview
    renderSlotsWithCartPreview(cartEquipment);
    
    // Render stats with cart preview (current + cart)
    renderStatsWithCartPreview(cartStats);

    console.log('[Equipment] Cart preview updated');
  }

  /**
   * Render equipment slots with cart preview overlay
   */
  function renderSlotsWithCartPreview(cartEquipment) {
    // Try both selectors for compatibility
    const slotsContainer = consoleEl.querySelector('[data-cs-equipment-slots]') || 
                          consoleEl.querySelector('.cs-equipment-grid');
    
    if (!slotsContainer) {
      console.error('[Equipment] Slots container not found');
      return;
    }

    console.log('[Equipment] Rendering slots with cart preview');

    // Define slot icons matching Cyberpunk style
    const slotIcons = {
      head: '?',
      chest: '?',
      legs: '?',
      hands: '?',
      feet: '?',
      accessory1: '?',
      accessory2: '?'
    };

    // Define left and right column slots
    const leftSlots = ['head', 'chest', 'legs'];
    const rightSlots = ['hands', 'feet', 'accessory1', 'accessory2'];

    // Generate left column HTML
    const leftColumnHTML = leftSlots.map(slotKey => {
      const slotConfig = SLOTS[slotKey];
      const equippedItem = currentLoadout[slotKey];
      const cartItems = cartEquipment[slotKey] || [];

      if (equippedItem || cartItems.length > 0) {
        const itemName = equippedItem ? equippedItem.title : cartItems[0]?.title || '';
        const displayName = itemName.length > 20 ? itemName.substring(0, 20) + '...' : itemName;
        
        return `
          <div class="cs-equipment-slot cs-slot-filled cs-slot-equipped ${cartItems.length > 0 ? 'cs-slot-cart-preview' : ''}" data-slot="${slotKey}">
            <span class="cs-slot-icon">${equippedItem?.pixelIcon || slotIcons[slotKey]}</span>
            <div class="cs-slot-info">
              <span class="cs-slot-label">${slotConfig.label.toUpperCase()}</span>
              ${equippedItem ? `<span class="cs-slot-item" data-full-name="${equippedItem.title}" title="${equippedItem.title}">${displayName}</span>` : ''}
              ${cartItems.map(item => {
                const cartItemName = item.title;
                const cartDisplayName = cartItemName.length > 18 ? cartItemName.substring(0, 18) + '...' : cartItemName;
                return `
                  <div class="cs-slot-cart-item" title="${cartItemName}">
                    <span class="cs-cart-badge">CART</span>
                    ${cartDisplayName} ${item.quantity > 1 ? `x${item.quantity}` : ''}
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `;
      } else {
        return `
          <div class="cs-equipment-slot cs-slot-empty" data-slot="${slotKey}">
            <span class="cs-slot-icon">${slotIcons[slotKey]}</span>
            <div class="cs-slot-info">
              <span class="cs-slot-label">${slotConfig.label.toUpperCase()}</span>
              <span class="cs-slot-status">[EMPTY]</span>
            </div>
          </div>
        `;
      }
    }).join('');

    // Generate right column HTML
    const rightColumnHTML = rightSlots.map(slotKey => {
      const slotConfig = SLOTS[slotKey];
      const equippedItem = currentLoadout[slotKey];
      const cartItems = cartEquipment[slotKey] || [];

      if (equippedItem || cartItems.length > 0) {
        const itemName = equippedItem ? equippedItem.title : cartItems[0]?.title || '';
        const displayName = itemName.length > 20 ? itemName.substring(0, 20) + '...' : itemName;
        
        return `
          <div class="cs-equipment-slot cs-slot-filled cs-slot-equipped ${cartItems.length > 0 ? 'cs-slot-cart-preview' : ''}" data-slot="${slotKey}">
            <span class="cs-slot-icon">${equippedItem?.pixelIcon || slotIcons[slotKey]}</span>
            <div class="cs-slot-info">
              <span class="cs-slot-label">${slotConfig.label.toUpperCase()}</span>
              ${equippedItem ? `<span class="cs-slot-item" data-full-name="${equippedItem.title}" title="${equippedItem.title}">${displayName}</span>` : ''}
              ${cartItems.map(item => {
                const cartItemName = item.title;
                const cartDisplayName = cartItemName.length > 18 ? cartItemName.substring(0, 18) + '...' : cartItemName;
                return `
                  <div class="cs-slot-cart-item" title="${cartItemName}">
                    <span class="cs-cart-badge">CART</span>
                    ${cartDisplayName} ${item.quantity > 1 ? `x${item.quantity}` : ''}
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `;
      } else {
        return `
          <div class="cs-equipment-slot cs-slot-empty" data-slot="${slotKey}">
            <span class="cs-slot-icon">${slotIcons[slotKey]}</span>
            <div class="cs-slot-info">
              <span class="cs-slot-label">${slotConfig.label.toUpperCase()}</span>
              <span class="cs-slot-status">[EMPTY]</span>
            </div>
          </div>
        `;
      }
    }).join('');

    // Combine into two columns
    slotsContainer.innerHTML = `
      <div class="cs-equipment-column cs-equipment-left">
        ${leftColumnHTML}
      </div>
      <div class="cs-equipment-column cs-equipment-right">
        ${rightColumnHTML}
      </div>
    `;

    console.log('[Equipment] Slots rendered in left/right columns');
  }

  /**
   * Render stats with cart preview (shows increase)
   */
  function renderStatsWithCartPreview(cartStats) {
    if (!consoleEl) return;

    // Get current equipped stats
    const equippedTotals = computeTotals(currentLoadout);

    console.log('[Equipment] Rendering stats with cart preview');
    console.log('[Equipment] Equipped stats:', equippedTotals);
    console.log('[Equipment] Cart stats:', cartStats);

    // Update each stat individually
    Object.entries(STATS).forEach(([statKey, statConfig]) => {
      const currentValue = equippedTotals[statKey] || 0;
      const cartBonus = cartStats[statKey] || 0;

      // Find stat value element
      const statEl = consoleEl.querySelector(`[data-cs-stat-${statKey}]`);
      if (statEl) {
        if (cartBonus > 0) {
          statEl.innerHTML = `${currentValue} <span class="cs-stat-cart-bonus">+${cartBonus}</span>`;
        } else {
          statEl.textContent = currentValue;
        }
        console.log(`[Equipment] Updated ${statKey}: ${currentValue} +${cartBonus}`);
      } else {
        console.warn(`[Equipment] Stat element not found for: ${statKey}`);
      }
    });
  }

  // Listen for cart updates
  document.addEventListener('cart:updated', () => {
    console.log('[Equipment] Cart updated, refreshing preview');
    updateCartPreview();
  });

  // Also listen for Shopify theme cart updates
  document.addEventListener('theme:cart:change', () => {
    console.log('[Equipment] Theme cart changed, refreshing preview');
    updateCartPreview();
  });

  /**
   * TEST FUNCTION - Hardcode an item in LEGS slot
   */
  function testHardcodedItem() {
    if (!consoleEl) {
      console.error('[Equipment] No console element found');
      return;
    }

    console.log('[Equipment] Testing hardcoded item in LEGS slot');

    // Hardcoded cart equipment
    const testCartEquipment = {
      legs: [{
        title: 'Recycled Tracksuit Trousers (TEST)',
        quantity: 1,
        isCart: true
      }]
    };

    // Hardcoded cart stats
    const testCartStats = {
      str: 2,
      vit: 3,
      style: 1
    };

    // Render with test data
    renderSlotsWithCartPreview(testCartEquipment);
    renderStatsWithCartPreview(testCartStats);

    console.log('[Equipment] Test item rendered');
  }

  // Expose public API
  window.CyberSells_equipment = {
    setupEquipmentConsole,
    equipItem,
    unequipSlot,
    computeTotals,
    computeMatchScore,
    getLoadout,
    clearLoadout,
    updateCartPreview,
    testHardcodedItem, // TEST FUNCTION
    SLOTS,
    STATS
  };

})();
